import { useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { PublicClient, ApiError } from "@forcebase-ai/api-client";
import { COGNITO_ENABLED, API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";
import { AuthLayout } from "../components/AuthLayout";

const SPECIALTIES = [
  "Internal Medicine",
  "Family Medicine",
  "Pediatrics",
  "Cardiology",
  "Endocrinology",
  "Dermatology",
  "Psychiatry",
];

// Provider create-account. On dev-sbx1 there's no Cognito — this form is the real provider
// APPLICATION intake (kind=doctor): it submits non-clinical credentialing identifiers to the public
// intake endpoint, landing a `submitted` row for Command to review (approval provisions the Healthie
// provider). No PHI is collected here (D5). In production there is NO self-serve staff signup:
// Cognito staff accounts are admin-created and require doctor group membership (DRS-3), so the
// route stays but shows a contact-admin gate.
export function CreateAccount() {
  const { isLoaded, isSignedIn } = useAppAuth();
  const publicApi = useMemo(() => new PublicClient({ baseUrl: API_ORIGIN }), []);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [npi, setNpi] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "submitted" | "error">("idle");
  const [error, setError] = useState("");

  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  if (COGNITO_ENABLED) {
    // Staff signup gate: provider accounts are created by an administrator (Cognito admin-create +
    // doctor group). Keep the route, point applicants at the practice.
    return (
      <AuthLayout tab="create">
        <div className="auth-form">
          <h2>
            Provider accounts are <em>admin-created.</em>
          </h2>
          <p>
            Champion Care provider accounts are provisioned by our clinical operations team. If
            you&rsquo;re joining the practice, contact your administrator to have your account
            created — you&rsquo;ll receive an invitation email with a temporary password.
          </p>
          <div className="auth-meta">
            Already have an account? <Link to="/sign-in">Sign in</Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (status === "submitting") return;
    if (!/^\d{10}$/.test(npi.trim())) {
      setError("NPI must be exactly 10 digits.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setError("");
    try {
      await publicApi.submitApplication({
        kind: "doctor",
        applicant_email: email.trim(),
        payload: {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          npi: npi.trim(),
          specialty,
          agreement_accepted: true,
        },
      });
      setStatus("submitted");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not submit your application.");
      setStatus("error");
    }
  }

  if (status === "submitted") {
    return (
      <AuthLayout tab="create">
        <div className="auth-form">
          <h2>
            Application <em>received.</em>
          </h2>
          <p>
            Thanks, {firstName || "Doctor"}. Our team will review your provider application and reach
            out at <strong>{email}</strong>. You&rsquo;ll be able to sign in once you&rsquo;re
            approved.
          </p>
        </div>
      </AuthLayout>
    );
  }

  const submitting = status === "submitting";

  return (
    <AuthLayout tab="create">
      <form className="auth-form" onSubmit={submit}>
        <h2>
          Join the <em>practice.</em>
        </h2>
        <p>Apply for a provider account — our team reviews each application.</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="input-group">
            <label htmlFor="fn">First name</label>
            <input
              id="fn"
              autoComplete="given-name"
              placeholder="Jane"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="ln">Last name</label>
            <input
              id="ln"
              autoComplete="family-name"
              placeholder="Forester"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="input-group">
          <label htmlFor="we">Work email</label>
          <input
            id="we"
            type="email"
            placeholder="dr.you@practice.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="input-group">
            <label htmlFor="npi">NPI number</label>
            <input
              id="npi"
              inputMode="numeric"
              maxLength={10}
              placeholder="10-digit NPI"
              value={npi}
              onChange={(e) => setNpi(e.target.value.replace(/\D/g, ""))}
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="spec">Specialty</label>
            <select
              id="spec"
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              required
            >
              <option value="" disabled>
                Select…
              </option>
              {SPECIALTIES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="auth-agree" style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: "var(--ink-2)", margin: "4px 0 8px" }}>
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            I have read and agree to the Provider Agreement and HIPAA Business Associate Agreement.
          </span>
        </label>

        {status === "error" && error && (
          <div className="auth-meta" role="alert" style={{ color: "var(--red)" }}>
            {error}
          </div>
        )}

        <button type="submit" className="btn btn-primary btn-lg" disabled={submitting || !agreed}>
          {submitting ? "Submitting…" : "Submit application"}
        </button>
        <div className="auth-meta">
          We only collect credentialing identifiers here — never any patient health information.
        </div>
      </form>
    </AuthLayout>
  );
}
